import { Box } from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { UnitGenerationPhase, UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';

type StatusDisplay = { label: string; colorScheme: string; icon?: React.ReactNode };

const RUNNING_ICON = (
  <Box
    as={RefreshCw}
    size={12}
    sx={{
      animation: 'spin 1s linear infinite',
      '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
    }}
  />
);

const STATUS_CONFIG: Record<UnitGenerationRunStatus, StatusDisplay> = {
  RUNNING: { label: 'Running', colorScheme: 'blue', icon: RUNNING_ICON },
  WAITING: { label: 'Waiting on review', colorScheme: 'yellow' },
  GENERATED: { label: 'Generated', colorScheme: 'gray' },
  IMPORTED: { label: 'Imported', colorScheme: 'gray' },
  FAILED: { label: 'Failed', colorScheme: 'red' },
  CANCELLED: { label: 'Cancelled', colorScheme: 'gray' },
};

const IMPORT_FAILED: StatusDisplay = {
  label: 'Import Failed',
  colorScheme: 'red',
};

export function getStatusDisplay(
  status: UnitGenerationRunStatus,
  error: string | null,
  gate?: ReviewGate | null,
): StatusDisplay {
  if (status === 'GENERATED' && error != null) {
    return IMPORT_FAILED;
  }

  if (status === UnitGenerationRunStatus.WAITING && gate != null) {
    return {
      ...STATUS_CONFIG[status],
      label:
        gate === 'allocation' ? 'Waiting on allocation review' : 'Waiting on assessment review',
    };
  }

  return STATUS_CONFIG[status];
}

export type ReviewGate = 'allocation' | 'assessment';

export function reviewGate(run: {
  status: UnitGenerationRunStatus;
  phase: UnitGenerationPhase | null;
}): ReviewGate | null {
  const waiting = run.status === UnitGenerationRunStatus.WAITING;
  const failed = run.status === UnitGenerationRunStatus.FAILED;

  if (
    (waiting && run.phase === UnitGenerationPhase.PLAN) ||
    (failed && run.phase === UnitGenerationPhase.ASSESSMENTS)
  ) {
    return 'allocation';
  }

  if (
    (waiting && run.phase === UnitGenerationPhase.ASSESSMENTS) ||
    (failed && run.phase === UnitGenerationPhase.CONTENT)
  ) {
    return 'assessment';
  }

  return null;
}
